import React, { useEffect, useRef, useState } from "react";
import SectionHeader from "./SectionHeader";
import { getStaffingColor, formatDeficit, formatNumber } from "./utils";
import EmployeeTooltip from "./EmployeeTooltip";

const staffingIcon = (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  </svg>
);

function PlanEditableCell({ department, isSaving, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const openEditor = () => {
    if (isSaving) return;
    setDraftValue(department.plan != null ? String(department.plan) : "");
    setIsEditing(true);
  };

  const closeEditor = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmed = draftValue.trim();
    if (!trimmed) {
      closeEditor();
      return;
    }

    const nextPlan = Number(trimmed);
    if (!Number.isInteger(nextPlan) || nextPlan < 0) {
      return;
    }

    if (department.plan === nextPlan) {
      closeEditor();
      return;
    }

    const isSaved = await onSave(department.id, nextPlan);
    if (isSaved) {
      closeEditor();
    }
  };

  if (isEditing) {
    return (
      <div className="hr-dashboard__plan-editor">
        <input
          ref={inputRef}
          type="number"
          min="0"
          step="1"
          className="hr-dashboard__plan-input"
          value={draftValue}
          disabled={isSaving}
          onChange={(event) => setDraftValue(event.target.value)}
          onBlur={handleSave}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSave();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              closeEditor();
            }
          }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="hr-dashboard__plan-button"
      onClick={openEditor}
      disabled={isSaving}
      title="Нажмите, чтобы указать план за выбранный месяц"
    >
      {isSaving ? "…" : formatNumber(department.plan)}
    </button>
  );
}

function StaffingTable({
  departments,
  totals,
  onPlanSave,
  planSaveError,
  tableHint,
  showsMonthlyAverage = false,
}) {
  const [savingDepartmentId, setSavingDepartmentId] = useState(null);

  const renderStaffingCell = (percent) => {
    if (percent == null) {
      return <span className="hr-dashboard__empty-value">—</span>;
    }

    const color = getStaffingColor(percent);
    return (
      <div className="hr-dashboard__staffing-cell">
        <div className="hr-dashboard__progress">
          <div
            className={`hr-dashboard__progress-fill hr-dashboard__progress-fill--${color}`}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
        <span className={`hr-dashboard__staffing-percent hr-dashboard__staffing-percent--${color}`}>
          {percent}%
        </span>
      </div>
    );
  };

  const handlePlanSave = async (departmentId, plan) => {
    if (!onPlanSave) return false;

    setSavingDepartmentId(departmentId);
    try {
      await onPlanSave(departmentId, plan);
      return true;
    } catch {
      return false;
    } finally {
      setSavingDepartmentId(null);
    }
  };

  return (
    <section className="hr-dashboard__card hr-dashboard__card--staffing">
      <SectionHeader
        icon={staffingIcon}
        iconTone="purple"
        title="Укомплектованность по отделениям"
        subtitle={
          showsMonthlyAverage
            ? "Средние помесячные значения: план, факт, дефицит и укомплектованность"
            : "План, факт, дефицит и укомплектованность"
        }
      />
      {planSaveError && (
        <div className="hr-dashboard__inline-error">{planSaveError}</div>
      )}
      {tableHint && (
        <p className="hr-dashboard__hint hr-dashboard__hint--info">{tableHint}</p>
      )}
      {departments.length === 0 ? (
        <p className="hr-dashboard__empty-state">Нет данных по отделениям</p>
      ) : (
        <div className="hr-dashboard__table-wrap">
          <table className="hr-dashboard__table hr-dashboard__table--staffing">
            <thead>
              <tr>
                <th>Отделение</th>
                <th title={showsMonthlyAverage ? "Среднее арифметическое плана по месяцам" : undefined}>
                  План
                </th>
                <th title={showsMonthlyAverage ? "Среднее арифметическое факта по месяцам" : undefined}>
                  Факт
                </th>
                <th title={showsMonthlyAverage ? "Среднее арифметическое дефицита по месяцам" : undefined}>
                  Дефицит
                </th>
                <th>Укомплектованность</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((dept) => (
                <tr key={dept.id || dept.code}>
                  <td>
                    <span className="hr-dashboard__dept-name-cell" title={dept.name}>
                      {dept.name || dept.code}
                    </span>
                  </td>
                  <td className="hr-dashboard__cell--num">
                    {onPlanSave ? (
                      <PlanEditableCell
                        department={dept}
                        isSaving={savingDepartmentId === dept.id}
                        onSave={handlePlanSave}
                      />
                    ) : (
                      <span className="hr-dashboard__cell-value">
                        {formatNumber(dept.plan)}
                      </span>
                    )}
                  </td>
                  <td className="hr-dashboard__cell--num">
                    <EmployeeTooltip title={dept.name} employees={dept.employees}>
                      <span className="hr-dashboard__cell-value">{dept.fact}</span>
                    </EmployeeTooltip>
                  </td>
                  <td
                    className={`hr-dashboard__cell--num${
                      dept.deficit != null && dept.deficit < 0
                        ? " hr-dashboard__deficit"
                        : ""
                    }`}
                  >
                    {dept.deficit != null ? formatDeficit(dept.deficit) : "—"}
                  </td>
                  <td>{renderStaffingCell(dept.staffingPercent)}</td>
                </tr>
              ))}
              <tr className="hr-dashboard__table-total">
                <td>
                  Итого
                  {totals.hasDepartmentOverlap && (
                    <span
                      className="hr-dashboard__total-note"
                      title="Факт и дефицит в Итого — по уникальным сотрудникам, а не сумма по отделениям"
                    >
                      *
                    </span>
                  )}
                </td>
                <td className="hr-dashboard__cell--num">
                  <span className="hr-dashboard__cell-value">
                    {formatNumber(totals.plan)}
                  </span>
                </td>
                <td className="hr-dashboard__cell--num">
                  <span className="hr-dashboard__cell-value">{totals.fact}</span>
                  {totals.hasDepartmentOverlap && (
                    <span className="hr-dashboard__total-unique-label">уник.</span>
                  )}
                </td>
                <td
                  className={`hr-dashboard__cell--num${
                    totals.deficit != null && totals.deficit < 0
                      ? " hr-dashboard__deficit"
                      : ""
                  }`}
                >
                  {totals.deficit != null ? formatDeficit(totals.deficit) : "—"}
                </td>
                <td>{renderStaffingCell(totals.staffingPercent)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {totals.hasDepartmentOverlap && (
        <p className="hr-dashboard__hint hr-dashboard__hint--overlap">
          * В Итого факт — {totals.fact} уникальных сотрудников (дефицит{" "}
          {totals.deficit != null ? formatDeficit(totals.deficit) : "—"}). Сумма
          факта по отделениям — {totals.departmentFactSum}: часть сотрудников
          числится в нескольких отделениях и учитывается в каждой строке.
        </p>
      )}
      {!totals.hasPlan && (
        <p className="hr-dashboard__hint">
          Штатный план не заполнен — укомплектованность и дефицит недоступны.
        </p>
      )}
    </section>
  );
}

export default StaffingTable;
